import React, { useContext, useEffect, useState } from "react";
import { HeatMapChart, IHeatMapChartData, IHeatMapChartProps, IVerticalStackedChartProps, IVSChartDataPoint, VerticalStackedBarChart } from "@fluentui/react-charting";
import { IGraphData, IOrgConfig } from "../../types";
import useScreenSizeDetector from "../useScreenSizeDetector";
import { OrbUserContext } from "../OrbUserContext";
import { OrbConfigUtilsService } from "../../services/OrbConfigUtilsService";
import { useHistory } from "react-router-dom";

interface GrRiskHeatMapChartProps {
    graphData: IGraphData;
}

interface IChartConfig {
    Main: {
        Title?: string;
        SubTitle?: string;
        TitlesMarginBottom: number,
        TitlesAlign: string,
        ShowDescription: "left" | "right" | "top" | "bottom";
        Height: number;
    };
    YAxis: {
        AxisTitle: string;
    };
    XAxis: {
        AxisTitle: string;
    };
}

export const GrRiskHeatMapChart = (props: GrRiskHeatMapChartProps): React.ReactElement => {
    const { isMobile, isMediumScreen, isLargeScreen } = useScreenSizeDetector();
    const defaultHeight: number = 500;
    const { orbConfig } = useContext(OrbUserContext);

    const [graphConfig, setGraphConfig] = useState<IChartConfig>();
    const [heatMapData, setHeatMapData] = useState<IHeatMapChartData[]>();

    const history = useHistory();

    const rootStyle = { width: `100%`, height: `${graphConfig?.Main?.Height ?? defaultHeight}px` };

    useEffect(() => {
        const adjustData = () => {
            const jsonData = props.graphData.jSonProperties;
            const chartConfig: IChartConfig = JSON.parse(jsonData);
            setGraphConfig(chartConfig);
            const updatedData = updateHeatMapDataWithRisks(HeatMapData, props.graphData.data);
            setHeatMapData(updatedData);
        };

        adjustData();
    }, [props.graphData]);

    const yPointMapping: { [key: string]: string } = {
        p1: 'Very Low',
        p2: 'Low',
        p3: 'Medium',
        p4: 'High',
        p5: 'Very High',
    };

    const xPointMapping: { [key: string]: string } = {
        p1: 'Very Unlikely (<10%)',
        p2: 'Unlikely (>10%-<35%)',
        p3: 'Possible (>35%-<65%)',
        p4: 'Likely (>65%-<90%)',
        p5: 'Very Likely (>90%)',
    };

    const yPoint: string[] = ['p1', 'p2', 'p3', 'p4', 'p5'];

    const xPoint: string[] = ['p1', 'p2', 'p3', 'p4', 'p5'];

    const handleCellClick = (probability: number, impactLevel: number, message: string) => {
        console.log('probability', probability, 'impact', impactLevel, 'message', message);
        history.push({
            pathname: `/a3/risks-with-filters/${probability}/${impactLevel}`,
            state: { message }
        });
    };


    const HeatMapData: IHeatMapChartProps['data'] = [
        {
            value: 1,
            legend: "Impact Very Low - Very Unlikely (<10%)",
            data: [{
                x: xPoint[0], y: yPoint[0], value: 1, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(1, 1, "Impact Very Low - Very Unlikely (<10%)")
            }],
        },
        {
            value: 2,
            legend: "Impact Very Low - Unlikely (>10%-<35%)",
            data: [{
                x: xPoint[1], y: yPoint[0], value: 2, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(2, 1, "Impact Very Low - Unlikely (>10%-<35%)")
            }],
        },
        {
            value: 3,
            legend: "Impact Very Low - Possible (>35%-<65%)",
            data: [{
                x: xPoint[2], y: yPoint[0], value: 3, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(3, 1, "Impact Very Low - Possible (>35%-<65%)")
            }],
        },
        {
            value: 4,
            legend: "Impact Very Low - Likely (>65%-<90%)",
            data: [{
                x: xPoint[3], y: yPoint[0], value: 4, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(4, 1, "Impact Very Low - Likely (>65%-<90%)")
            }],
        },
        {
            value: 5,
            legend: "Impact Very Low - Very Likely (>90%)",
            data: [{
                x: xPoint[4], y: yPoint[0], value: 5, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(5, 1, "Impact Very Low - Very Likely (>90%)")
            }],
        },
        {
            value: 6,
            legend: "Impact Low - Very Unlikely (<10%)",
            data: [{
                x: xPoint[0], y: yPoint[1], value: 6, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(1, 2, "Impact Low - Very Unlikely (<10%)")
            }],
        },
        {
            value: 7,
            legend: "Impact Low - Unlikely (>10%-<35%)",
            data: [{
                x: xPoint[1], y: yPoint[1], value: 7, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(2, 2, "Impact Low - Unlikely (>10%-<35%)")
            }],
        },
        {
            value: 8,
            legend: "Impact Low - Possible (>35%-<65%)",
            data: [{
                x: xPoint[2], y: yPoint[1], value: 8, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(3, 2, "Impact Low - Possible (>35%-<65%)")
            }],
        },
        {
            value: 9,
            legend: "Impact Low - Likely (>65%-<90%)",
            data: [{
                x: xPoint[3], y: yPoint[1], value: 9, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(4, 2, "Impact Low - Likely (>65%-<90%)")
            }],
        },
        {
            value: 10,
            legend: "Impact Low - Very Likely (>90%)",
            data: [{
                x: xPoint[4], y: yPoint[1], value: 10, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(5, 2, "Impact Low - Very Likely (>90%)")
            }],
        },
        {
            value: 11,
            legend: "Impact Medium - Very Unlikely (<10%)",
            data: [{
                x: xPoint[0], y: yPoint[2], value: 11, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(1, 3, "Impact Medium - Very Unlikely (<10%)")
            }],
        },
        {
            value: 12,
            legend: "Impact Medium - Unlikely (>10%-<35%)",
            data: [{
                x: xPoint[1], y: yPoint[2], value: 12, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(2, 3, "Impact Medium - Unlikely (>10%-<35%)")
            }],
        },
        {
            value: 13,
            legend: "Impact Medium - Possible (>35%-<65%)",
            data: [{
                x: xPoint[2], y: yPoint[2], value: 13, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(3, 3, "Impact Medium - Possible (>35%-<65%)")
            }],
        },
        {
            value: 14,
            legend: "Impact Medium - Likely (>65%-<90%)",
            data: [{
                x: xPoint[3], y: yPoint[2], value: 14, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(4, 3, "Impact Medium - Likely (>65%-<90%)")
            }],
        },
        {
            value: 15,
            legend: "Impact Medium - Very Likely (>90%)",
            data: [{
                x: xPoint[4], y: yPoint[2], value: 15, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(5, 3, "Impact Medium - Very Likely (>90%)")
            }],
        },
        {
            value: 16,
            legend: "Impact High - Very Unlikely (<10%)",
            data: [{
                x: xPoint[0], y: yPoint[3], value: 16, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(1, 4, "Impact High - Very Unlikely (<10%)")
            }],
        },
        {
            value: 17,
            legend: "Impact High - Unlikely (>10%-<35%)",
            data: [{
                x: xPoint[1], y: yPoint[3], value: 17, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(2, 4, "Impact High - Unlikely (>10%-<35%)")
            }],
        },
        {
            value: 18,
            legend: "Impact High - Possible (>35%-<65%)",
            data: [{
                x: xPoint[2], y: yPoint[3], value: 18, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(3, 4, "Impact High - Possible (>35%-<65%)")
            }],
        },
        {
            value: 19,
            legend: "Impact High - Likely (>65%-<90%)",
            data: [{
                x: xPoint[3], y: yPoint[3], value: 19, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(4, 4, "Impact High - Likely (>65%-<90%)")
            }],
        },
        {
            value: 20,
            legend: "Impact High - Very Likely (>90%)",
            data: [{
                x: xPoint[4], y: yPoint[3], value: 20, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(5, 4, "Impact High - Very Likely (>90%)")
            }],
        },
        {
            value: 21,
            legend: "Impact Very High - Very Unlikely (<10%)",
            data: [{
                x: xPoint[0], y: yPoint[4], value: 21, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(1, 5, "Impact Very High - Very Unlikely (<10%)")
            }],
        },
        {
            value: 22,
            legend: "Impact Very High - Unlikely (>10%-<35%)",
            data: [{
                x: xPoint[1], y: yPoint[4], value: 22, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(2, 5, "Impact Very High - Unlikely (>10%-<35%)")
            }],
        },
        {
            value: 23,
            legend: "Impact Very High - Possible (>35%-<65%)",
            data: [{
                x: xPoint[2], y: yPoint[4], value: 23, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(3, 5, "Impact Very High - Possible (>35%-<65%)")
            }],
        },
        {
            value: 24,
            legend: "Impact Very High - Likely (>65%-<90%)",
            data: [{
                x: xPoint[3], y: yPoint[4], value: 24, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(4, 5, "Impact Very High - Likely (>65%-<90%)")
            }],
        },
        {
            value: 25,
            legend: "Impact Very High - Very Likely (>90%)",
            data: [{
                x: xPoint[4], y: yPoint[4], value: 25, rectText: '', descriptionMessage: '',
                onClick: () => handleCellClick(5, 5, "Impact Very High - Very Likely (>90%)")
            }],
        },
    ];


    const getCellValue = (xIndex: number, yIndex: number): number => {
        return yIndex * 5 + xIndex + 1;
    };

    const updateHeatMapDataWithRisks = (
        heatMapData: IHeatMapChartProps['data'],
        risks: any[]
    ): IHeatMapChartProps['data'] => {
        // Map to hold counts per cell
        const cellRiskMap = new Map<number, { target: number; unmitigated: number }>();

        // Step 1: Process all risks
        for (const risk of risks) {
            // Handle unmitigated values safely
            if (
                risk.UnmitigatedRiskProbabilityID != null &&
                risk.UnmitigatedRiskImpactLevelID != null
            ) {
                const unmitigatedCell = getCellValue(
                    risk.UnmitigatedRiskProbabilityID - 1,
                    risk.UnmitigatedRiskImpactLevelID - 1
                );

                if (!cellRiskMap.has(unmitigatedCell)) {
                    cellRiskMap.set(unmitigatedCell, { target: 0, unmitigated: 0 });
                }
                cellRiskMap.get(unmitigatedCell)!.unmitigated += 1;
            }

            // Handle target values safely
            if (
                risk.TargetRiskProbabilityID != null &&
                risk.TargetRiskImpactLevelID != null
            ) {
                const targetCell = getCellValue(
                    risk.TargetRiskProbabilityID - 1,
                    risk.TargetRiskImpactLevelID - 1
                );

                if (!cellRiskMap.has(targetCell)) {
                    cellRiskMap.set(targetCell, { target: 0, unmitigated: 0 });
                }
                cellRiskMap.get(targetCell)!.target += 1;
            }
        }

        // Step 2: Update heat map data
        const updatedHeatMapData = heatMapData.map((cell) => {
            const counts = cellRiskMap.get(cell.value);

            if (counts) {
                //const rectText = `T${counts.target}-U${counts.unmitigated}`;
                const rectText = [
                    counts.target ? `T${counts.target}` : "",
                    counts.unmitigated ? `U${counts.unmitigated}` : ""
                ].filter(Boolean).join("-");

                const targetText = `${counts.target} Target Risk${counts.target !== 1 ? 's' : ''}`;
                const unmitigatedText = `${counts.unmitigated} Unmitigated Risk${counts.unmitigated !== 1 ? 's' : ''}`;
                const descriptionMessage = `${targetText} and ${unmitigatedText}. Click cell for more details.`;

                return {
                    ...cell,
                    data: cell.data.map((d) => ({
                        ...d,
                        rectText,
                        descriptionMessage,
                    })),
                };
            }

            // No risks in this cell – keep defaults
            return {
                ...cell,
                data: cell.data.map((d) => ({
                    ...d,
                    rectText: '',
                    descriptionMessage: '',
                })),
            };
        });

        return updatedHeatMapData;
    };

    const getChartWidth = (): number => {
        if (isMobile) {
            return window.innerWidth * 0.9;
        } else if (isMediumScreen) {
            return window.innerWidth * 0.8;
        } else {
            return window.innerWidth * 0.7;
        }
    };

    const titleTextAlign: React.CSSProperties['textAlign'] =
        ['start', 'end', 'left', 'right', 'center', 'justify', 'match-parent'].includes(graphConfig?.Main?.TitlesAlign?.toLocaleLowerCase())
            ? graphConfig.Main.TitlesAlign as React.CSSProperties['textAlign']
            : 'left';

    const titleMarginBottom: string =
        graphConfig?.Main?.TitlesMarginBottom !== undefined
            ? `${graphConfig.Main.TitlesMarginBottom}px`
            : '20px';

    return (
        <div style={rootStyle}>
            {graphConfig && heatMapData && (
                <>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: titleMarginBottom,
                            //flexDirection: isMobile ? 'column' : 'row', // Change to column on mobile                            
                            //alignItems: isMobile ? 'flex-start' : 'center', // Align title and buttons separately on mobile
                        }}
                    >
                        {graphConfig.Main.Title && graphConfig.Main.Title.length > 0 && (
                            <div
                                style={{
                                    flex: 'auto',
                                    //marginBottom: isMobile ? '10px' : '0'
                                }}>
                                <div style={{ marginBottom: '10px' }}>
                                    <div style={{ textAlign: titleTextAlign }}>{OrbConfigUtilsService.checkOrbTokens(graphConfig.Main.Title, orbConfig)}</div>
                                    <div style={{ fontSize: 'small', textAlign: titleTextAlign }}>{OrbConfigUtilsService.checkOrbTokens(graphConfig.Main.SubTitle, orbConfig)}</div>
                                </div>
                            </div>
                        )}


                    </div>

                    {1 + 1 === 2 ? (
                        <HeatMapChart
                            culture={window.navigator.language}
                            chartTitle={OrbConfigUtilsService.checkOrbTokens(graphConfig.Main.Title, orbConfig)}
                            data={heatMapData}
                            xAxisTitle={graphConfig.XAxis.AxisTitle}
                            yAxisTitle={graphConfig.YAxis.AxisTitle}
                            wrapXAxisLables={true}
                            showXAxisLablesTooltip={false}
                            // eslint-disable-next-line react/jsx-no-bind
                            yAxisStringFormatter={(point: string) => yPointMapping[point as string]}
                            xAxisStringFormatter={(point: string) => xPointMapping[point as string]}
                            // xAxisNumberFormatString=".7s"
                            // yAxisNumberFormatString=".3s"
                            height={graphConfig.Main?.Height ?? defaultHeight}
                            width={getChartWidth()}
                            domainValuesForColorScale={[
                                1, 2, 3, 4, 5,
                                6, 7, 8, 9, 10,
                                11, 12, 13, 14, 15,
                                16, 17, 18, 19, 20,
                                21, 22, 23, 24, 25
                            ]}
                            rangeValuesForColorScale={[
                                // Very Low Impact (bottom row)
                                '#006837',  // Very Unlikely
                                '#2DA254',
                                '#87CB66',
                                '#CDE983',
                                '#FEFEBE',  // Very Likely
                                // Low Impact
                                '#2DA254',
                                '#87CB66',
                                '#CDE983',
                                '#FEFEBE',
                                '#FCD27E',
                                // Moderate Impact
                                '#87CB66',
                                '#CDE983',
                                '#FEFEBE',
                                '#FCD27E',
                                '#F88B52',
                                // High Impact
                                '#CDE983',
                                '#FEFEBE',
                                '#FCD27E',
                                '#F88B52',
                                '#DD3D2D',
                                // Very High Impact (top row)
                                '#FEFEBE',
                                '#FCD27E',
                                '#F88B52',
                                '#DD3D2D',
                                '#A50126'
                            ]}
                            hideLegend={true}
                            enableReflow={true}
                            margins={{ /*top: 20, */bottom: 50, left: 100/*, right: 10*/ }}  // <-- Increase left margin here
                            className="riskHeatmap"
                        />
                    ) : (
                        <div style={{ textAlign: 'center', padding: '1rem', color: '#555' }}>
                            No data available
                        </div>
                    )}

                </>
            )}
        </div>
    );
};
